import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail } from "lucide-react";

const GOLD = "#C9A24B";
const CREAM = "#F6F3EC";
const INK = "#1C1A17";
const EMAIL = "infodogana2.0@gmail.com";
const UPDATED = "21 ottobre 2026";

function useMeta(title, desc) {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    const prev = m.getAttribute("content");
    m.setAttribute("content", desc);
    return () => { if (prev) m.setAttribute("content", prev); };
  }, [title, desc]);
}

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg md:text-xl font-semibold" style={{ color: INK }}>{title}</h2>
    <div className="mt-2 text-[15px] leading-relaxed text-black/70 space-y-2">{children}</div>
  </section>
);

export default function Privacy() {
  useMeta("Privacy Policy · Dogana 2.0", "Informativa privacy di Dogana 2.0 (dogana2.it): finalità, dati trattati, base giuridica, integrazione WhatsApp Business, diritti GDPR e come richiedere la cancellazione dei dati.");
  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM, fontFamily: "'Outfit', sans-serif" }} data-testid="privacy-page">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2" style={{ color: GOLD }}>
          <ShieldCheck className="w-6 h-6" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">Dogana 2.0</span>
        </div>
        <h1 className="mt-3 text-3xl md:text-4xl font-bold" style={{ color: INK }}>Privacy Policy</h1>
        <p className="mt-2 text-sm text-black/50">Ultimo aggiornamento: {UPDATED}</p>

        <Section title="Titolare del trattamento">
          <p>Il titolare del trattamento è <strong>Dogana 2.0</strong> (sito: <a className="underline" href="https://dogana2.it">dogana2.it</a>). Per qualsiasi richiesta relativa ai tuoi dati personali puoi scrivere a <a className="underline" href={`mailto:${EMAIL}`} style={{ color: "#8a6d2b" }}>{EMAIL}</a>.</p>
        </Section>

        <Section title="Finalità del trattamento">
          <ul className="list-disc pl-5 space-y-1">
            <li>Gestione delle richieste dei clienti e assistenza</li>
            <li>Prenotazioni</li>
            <li>Ordini e self-order, se attivi</li>
            <li>CRM e storico cliente</li>
            <li>Programma fedeltà/premi, quando attivato</li>
            <li>Invio di comunicazioni via WhatsApp richieste o consentite</li>
            <li>Sondaggi e raccolta di feedback</li>
            <li>Sicurezza e prevenzione di abusi</li>
          </ul>
        </Section>

        <Section title="Categorie di dati trattati">
          <ul className="list-disc pl-5 space-y-1">
            <li>Nome</li>
            <li>Numero di telefono</li>
            <li>Email, se fornita</li>
            <li>Dati di prenotazione</li>
            <li>Acquisti/ordini/importi, se collegati al CRM</li>
            <li>Preferenze e feedback</li>
            <li>Dati tecnici minimi necessari al funzionamento del servizio</li>
          </ul>
        </Section>

        <Section title="Base giuridica">
          <ul className="list-disc pl-5 space-y-1">
            <li>Esecuzione del servizio richiesto</li>
            <li>Consenso dell'interessato, dove richiesto</li>
            <li>Legittimo interesse per sicurezza e gestione tecnica</li>
            <li>Adempimento di obblighi di legge</li>
          </ul>
        </Section>

        <Section title="WhatsApp e Meta">
          <p>Alcune comunicazioni con i clienti possono essere gestite tramite <strong>WhatsApp Business Platform</strong> (Meta Platforms Ireland Ltd.), che agisce come fornitore tecnico del canale di messaggistica. Meta <strong>non</strong> è titolare del CRM di Dogana 2.0 e non gestisce per proprio conto i dati clienti del locale: il trattamento dei dati a fini di gestione clienti resta in capo a Dogana 2.0.</p>
        </Section>

        <Section title="Conservazione dei dati">
          <p>I dati sono conservati solo per il tempo necessario alle finalità sopra indicate e al rispetto degli obblighi di legge applicabili. Al venir meno di tali finalità i dati vengono cancellati o anonimizzati.</p>
        </Section>

        <Section title="Destinatari e fornitori tecnici">
          <p>I dati possono essere trattati da fornitori tecnici che supportano il funzionamento del servizio (ad esempio piattaforma di messaggistica WhatsApp/Meta, servizi di invio email e hosting), limitatamente a quanto necessario e nel rispetto della normativa applicabile.</p>
        </Section>

        <Section title="Trasferimenti extra-UE">
          <p>Alcuni fornitori tecnici potrebbero trattare dati al di fuori dell'Unione Europea. In tali casi i trasferimenti avvengono adottando garanzie adeguate previste dalla normativa applicabile (ad esempio clausole contrattuali standard).</p>
        </Section>

        <Section title="I tuoi diritti (GDPR)">
          <p>In qualità di interessato puoi esercitare i seguenti diritti:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Accesso ai dati</li>
            <li>Rettifica</li>
            <li>Cancellazione</li>
            <li>Limitazione del trattamento</li>
            <li>Opposizione</li>
            <li>Portabilità, ove applicabile</li>
            <li>Revoca del consenso in qualsiasi momento</li>
          </ul>
          <p>Per esercitare i tuoi diritti scrivi a <a className="underline" href={`mailto:${EMAIL}`} style={{ color: "#8a6d2b" }}>{EMAIL}</a>.</p>
        </Section>

        <Section title="Come richiedere la cancellazione dei dati">
          <p>Puoi richiedere in qualsiasi momento la cancellazione dei tuoi dati personali tramite la pagina dedicata:{" "}
            <Link to="/eliminazione-dati" className="underline font-medium" style={{ color: "#8a6d2b" }} data-testid="link-to-deletion">Richiesta di eliminazione dei dati</Link>.
          </p>
        </Section>

        <Section title="Aggiornamenti della presente informativa">
          <p>La presente informativa può essere aggiornata nel tempo. Le modifiche saranno pubblicate su questa pagina con la relativa data di ultimo aggiornamento.</p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-black/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <Link to="/privacy" className="underline" style={{ color: INK }}>Privacy Policy</Link>
          <Link to="/eliminazione-dati" className="underline" style={{ color: INK }}>Eliminazione dati</Link>
          <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-1 text-black/60"><Mail className="w-4 h-4" /> {EMAIL}</a>
        </footer>
      </div>
    </div>
  );
}
